// ConsultaPaciente.js - Rediseño Moderno (moustomp)
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
  IconButton, Tooltip, Chip, Avatar, Stack,
  Modal, Dialog, DialogTitle, DialogContent,
  Divider,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  LocalPharmacy as LocalPharmacyIcon,
  CalendarToday, Pets,
  Close as CloseIcon,
} from '@mui/icons-material';

import api from '../api/api';
import ProgramacionConsulta from './ProgramacionConsulta';
import Vademecum from './Vademecum';
import { calcularEdadDetallada } from '../utils/dateUtils';
import HistorialConsultas from './HistorialConsultas';
import DetalleConsulta from './DetalleConsulta';
import QrPrivadoButton from './QrPrivadoButton';

// ─── Paleta compartida ────────────────────────────────────────────────────────
export const THEME = {
  primary: '#1a6b4a',
  primaryLight: '#e8f5ee',
  primaryMid: '#2e8b62',
  accent: '#f0a500',
  accentLight: '#fff8e6',
  danger: '#d64045',
  dangerLight: '#fdecea',
  surface: '#ffffff',
  surfaceAlt: '#f7f9f8',
  border: '#e0ebe6',
  borderDark: '#c5d9d0',
  textPrimary: '#0f2d1e',
  textSecondary: '#5a7a6e',
  textMuted: '#93b0a5',
  shadow: '0 2px 12px rgba(26,107,74,0.08)',
  shadowMd: '0 4px 24px rgba(26,107,74,0.12)',
  shadowLg: '0 8px 40px rgba(26,107,74,0.16)',
};

const ConsultaPaciente = ({ visible, onClose, id_paciente, id_cita }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [citas, setCitas] = useState([]);
  const [selectedCita, setSelectedCita] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showProgramar, setShowProgramar] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isVademecumOpen, setIsVademecumOpen] = useState(false);
  const [pacienteNombre, setPacienteNombre] = useState('');
  const [razaNombre, setRazaNombre] = useState('');
  const [pacienteEdad, setPacienteEdad] = useState('');
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewContent, setQuickViewContent] = useState(null);

  const fetchCitasConConsultas = useCallback(async () => {
    if (!id_paciente) return;
    try {
      setLoading(true);
      const res = await api.get(`/citas/con-consultas/${id_paciente}`);
      const uniqueCitas = res.data.filter((cita, index, self) =>
        index === self.findIndex((c) => c.id_cita === cita.id_cita)
      );
      const sortedCitas = uniqueCitas.sort((a, b) => b.id_cita - a.id_cita);
      setCitas(sortedCitas);

      if (id_cita && sortedCitas.length > 0) {
        const cita = sortedCitas.find((c) => c.id_cita === id_cita);
        if (cita) { setSelectedCita(cita); setLoading(false); return; }
      }
      if (sortedCitas.length > 0) setSelectedCita(sortedCitas[0]);
      setLoading(false);
    } catch (err) {
      setError('Error al cargar citas: ' + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  }, [id_paciente, id_cita]);

  const fetchPacienteYRaza = useCallback(async () => {
    try {
      const pacienteRes = await api.get(`/pacientes/${id_paciente}`);
      setPacienteNombre(pacienteRes.data.nombre_paciente || 'N/A');
      setPacienteEdad(calcularEdadDetallada(pacienteRes.data.fecha_nac_paciente));
      const razaRes = await api.get(`/razas/${pacienteRes.data.id_raza}`);
      setRazaNombre(razaRes.data.nombre_raza || 'N/A');
    } catch { setError('Error al cargar datos del paciente'); }
  }, [id_paciente]);

  // Limpiar estado al cambiar de paciente
  useEffect(() => {
    setCitas([]);
    setSelectedCita(null);
    setError(null);
  }, [id_paciente]);

  useEffect(() => {
    if (visible) { fetchCitasConConsultas(); fetchPacienteYRaza(); }
  }, [visible, fetchCitasConConsultas, fetchPacienteYRaza]);

  const handleConsultaSaved = async () => { await fetchCitasConConsultas(); setShowProgramar(false); };

  const handleClose = () => {
    setIsMaximized(false); setSelectedCita(null);
    setError(null); setShowProgramar(false); setQuickViewOpen(false); onClose();
  };

  const handleQuickView = (cita) => {
    if (!cita.consulta) return;
    setQuickViewContent(cita.consulta);
    setQuickViewOpen(true);
  };

  if (!visible) return null;
  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 8, gap: 2 }}>
      <CircularProgress size={48} sx={{ color: THEME.primary }} />
      <Typography sx={{ color: THEME.textSecondary, fontSize: '14px' }}>Cargando consultas...</Typography>
    </Box>
  );
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{
      bgcolor: THEME.surfaceAlt,
      borderRadius: isMaximized ? 0 : '16px',
      overflow: 'hidden',
      position: isMaximized ? 'fixed' : 'relative',
      top: 0, left: 0, right: 0, bottom: 0,
      width: isMaximized ? '100vw' : '100%',
      height: isMaximized ? '100vh' : 'auto',
      zIndex: isMaximized ? 300 : 'auto',
      display: 'flex', flexDirection: 'column',
      boxShadow: isMaximized ? 'none' : THEME.shadowLg,
      border: `1px solid ${THEME.border}`,
    }}>

      {/* ── CABECERA ── */}
      <Box sx={{
        px: 3, py: 2, bgcolor: THEME.surface,
        borderBottom: `1px solid ${THEME.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: THEME.primaryLight, border: `2px solid ${THEME.primary}` }}>
            <Pets sx={{ color: THEME.primary, fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: THEME.textPrimary, lineHeight: 1.2 }}>
              {pacienteNombre}
            </Typography>
            <Stack direction="row" spacing={1} mt={0.6}>
              <Chip label={razaNombre} size="small" sx={{ height: 20, fontSize: '11px', fontWeight: 600, bgcolor: THEME.primaryLight, color: THEME.primary, border: `1px solid ${THEME.primaryMid}` }} />
              <Chip
                icon={<CalendarToday sx={{ fontSize: '11px !important', color: `${THEME.textSecondary} !important` }} />}
                label={pacienteEdad || 'Edad no disponible'} size="small"
                sx={{ height: 20, fontSize: '11px', bgcolor: THEME.surfaceAlt, color: THEME.textSecondary, border: `1px solid ${THEME.border}` }}
              />
              <Chip
                label={`${citas.length} consulta${citas.length !== 1 ? 's' : ''}`} size="small"
                sx={{ height: 20, fontSize: '11px', fontWeight: 700, bgcolor: THEME.accentLight, color: THEME.accent }}
              />
            </Stack>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Vademécum">
            <IconButton onClick={() => setIsVademecumOpen(true)} sx={{ color: THEME.textSecondary, '&:hover': { bgcolor: THEME.primaryLight, color: THEME.primary } }}>
              <LocalPharmacyIcon />
            </IconButton>
          </Tooltip>
          <QrPrivadoButton id_paciente={id_paciente} pacienteNombre={pacienteNombre} />
          <Tooltip title={isMaximized ? 'Restaurar' : 'Maximizar'}>
            <IconButton onClick={() => setIsMaximized(!isMaximized)} sx={{ color: THEME.textSecondary }}>
              {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Button
            variant="contained" startIcon={<AddIcon />}
            onClick={() => setShowProgramar(true)} size="small"
            sx={{
              bgcolor: THEME.primary, color: '#fff', borderRadius: '9px',
              textTransform: 'none', fontWeight: 700, fontSize: '13px',
              px: 2, py: 0.8, boxShadow: 'none',
              '&:hover': { bgcolor: THEME.primaryMid },
            }}
          >
            Nueva Consulta
          </Button>
          <IconButton onClick={handleClose} sx={{ color: THEME.textMuted, '&:hover': { color: THEME.danger } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ── CUERPO: dos columnas ── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Historial (izquierda) */}
        <Box sx={{
          width: 240, flexShrink: 0,
          display: 'flex', flexDirection: 'column',
          bgcolor: THEME.surface,
          borderRight: `1px solid ${THEME.border}`,
          overflow: 'hidden',
        }}>
          <HistorialConsultas
            citas={citas}
            selectedCita={selectedCita}
            onSelectCita={setSelectedCita}
            onQuickView={isMobile ? handleQuickView : undefined}
            onRefresh={fetchCitasConConsultas}
            loading={loading}
          />
        </Box>

        {/* Detalle (derecha) */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {selectedCita ? (
            <DetalleConsulta
              selectedCita={selectedCita}
              citas={citas}
              setCitas={setCitas}
              id_paciente={id_paciente}
              pacienteNombre={pacienteNombre}
              razaNombre={razaNombre}
              onRefresh={fetchCitasConConsultas}
              setError={setError}
            />
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
              <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: THEME.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Pets sx={{ fontSize: 32, color: THEME.primary }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: THEME.textPrimary, mb: 0.5 }}>
                  Selecciona una consulta
                </Typography>
                <Typography sx={{ fontSize: '13px', color: THEME.textSecondary }}>
                  Elige una del historial o registra una nueva
                </Typography>
              </Box>
              <Button
                variant="outlined" startIcon={<AddIcon />}
                onClick={() => setShowProgramar(true)}
                sx={{ mt: 1, borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '13px', borderColor: THEME.primary, color: THEME.primary, '&:hover': { bgcolor: THEME.primaryLight } }}
              >
                Nueva consulta
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── DIALOG RESUMEN RÁPIDO MÓVIL ── */}
      <Dialog open={quickViewOpen} onClose={() => setQuickViewOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '16px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px', fontWeight: 700, color: THEME.textPrimary }}>
          Resumen de Consulta
          <IconButton onClick={() => setQuickViewOpen(false)} size="small" sx={{ color: THEME.textMuted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: THEME.border }}>
          {quickViewContent ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: 'Diagnóstico', value: quickViewContent.diagnostico_patologia },
                { label: 'Tratamiento en clínica', value: quickViewContent.tratamiento_clinica },
                { label: 'Tratamiento domiciliario', value: quickViewContent.tratamiento },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '14px', color: value ? THEME.textPrimary : THEME.textMuted, fontStyle: value ? 'normal' : 'italic' }}>
                    {value || 'Sin datos'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : <Typography>Sin información disponible</Typography>}
        </DialogContent>
      </Dialog>

      {/* ── MODAL PROGRAMAR ── */}
      <Modal open={showProgramar} onClose={() => setShowProgramar(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: 700 }, bgcolor: 'background.paper',
          borderRadius: '16px', boxShadow: 24, p: 4, maxHeight: '95vh', overflowY: 'auto',
        }}>
          <ProgramacionConsulta visible={showProgramar} onClose={handleConsultaSaved} id_paciente={id_paciente} />
        </Box>
      </Modal>
 {/* ── MODAL VADEMÉCUM MEJORADO ── */}
      <Modal
        open={isVademecumOpen}
        onClose={() => setIsVademecumOpen(false)}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.25)' }
          }
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: { xs: '100%', md: 820 },
          bgcolor: THEME.surface,
          boxShadow: `-8px 0 40px rgba(0,0,0,0.15)`,
          display: 'flex',
          flexDirection: 'column',
          borderLeft: `1px solid ${THEME.border}`,
          outline: 'none',
        }}>
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            borderBottom: `1px solid ${THEME.border}` 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalPharmacyIcon sx={{ color: THEME.primary }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: THEME.textPrimary }}>
                Vademécum
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<CloseIcon />}
              onClick={() => setIsVademecumOpen(false)}
              sx={{ 
                bgcolor: THEME.danger, 
                '&:hover': { bgcolor: '#b5373b' }, 
                borderRadius: '8px' 
              }}
            >
              Cerrar
            </Button>
          </Box>

          {/* Contenido */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            <Vademecum isDrawerContext={true} />
          </Box>
        </Box>
      </Modal>

    </Box>
  );
};

export default React.memo(ConsultaPaciente);